import { type Donation, type SocialCause } from '@koala/sdk';
import { fetchSocialCauses, donateToSocialCause } from '@koala/sdk/v4';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useStrings } from '@/features/configs/strings';
import { useGlobalDispatch } from '@/redux/global/reducer';
import meActions from '@/redux/me/actions';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';
import * as ErrorReporter from '@/utils/errorReporter';
import { prepareErrorMessage } from '@/utils/global';
import { safelyGetString } from '@/utils/stringHelpers';

interface DonateToSocialCauseInput {
  socialCause: Pick<SocialCause, 'id' | 'name'>;
  donation: Donation;
}

export const useSocialCauses = () => {
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });
  return useQuery({
    queryKey: ['social-causes'],
    queryFn: () => fetchSocialCauses({ client }),
    refetchOnWindowFocus: false,
  });
};

export const useDonateToSocialCause = () => {
  const { dispatch, displayErrorToast, displayToast } = useGlobalDispatch();
  const strings = useStrings();
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });

  return useMutation(
    (input: DonateToSocialCauseInput) =>
      donateToSocialCause(input.socialCause.id, input.donation, { client }),
    {
      onError: async (error) => {
        ErrorReporter.captureException(error);

        const errorMessage = await prepareErrorMessage(
          safelyGetString(strings, 'rewards.donate_reward_error_text'),
          error,
        );

        dispatch(displayErrorToast(errorMessage.message));
      },
      onSuccess: (_data, variables) => {
        // If user has successfully donated to a social cause, we need to refetch rewards to update the UI
        dispatch(meActions.fetchRedeemables());
        dispatch(meActions.fetchOffers());
        dispatch(meActions.fetchLoyaltyState());

        dispatch(
          displayToast(
            safelyGetString(strings, 'rewards.donate_reward_success_text', {
              socialCauseName: variables.socialCause.name,
            }),
          ),
        );
      },
    },
  );
};
